// FormEditorQuill.jsx
import React, { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ErrorMessage from './ErrorMessage';

// --- Font custom ---
const Font = Quill.import('formats/font');
Font.whitelist = ['sans-serif', 'serif', 'monospace'];
Quill.register(Font, true);

// --- Size custom ---
const Size = Quill.import('formats/size');
Size.whitelist = ['small', false, 'large', 'huge'];
Quill.register(Size, true);

const Video = Quill.import('formats/video');
class CustomVideo extends Video {
  static create(value) {
    let node = super.create(value);
    node.setAttribute('width', '100%');
    node.setAttribute('height', '800px');
    node.setAttribute('style', 'display: block; margin: 0 auto; border-radius: 8px;');
    return node;
  }
}
Quill.register(CustomVideo, true);

const FormEditorQuill = ({
  field,
  validation,
  placeholder,
  className = '',
  disabled = false,
  height = 1000,
  width = '100%',
  handleUploadImageDesc,
}) => {
  const methods = useFormContext();
  const { error } = methods.getFieldState(field, methods.formState);
  const quillRef = useRef(null);
  const imageUploadRef = useRef(handleUploadImageDesc);
  const setUploadErrorRef = useRef(methods.setError);

  useEffect(() => {
    imageUploadRef.current = handleUploadImageDesc;
    setUploadErrorRef.current = methods.setError;
  }, [handleUploadImageDesc, methods.setError]);

  useEffect(() => {
    methods.register(field, validation);
  }, [methods, field, validation]);

  useEffect(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;

    const h = typeof height === 'number' ? `${height}px` : height;
    editor.root.style.minHeight = h;
    editor.root.style.maxHeight = '10000px';
    editor.root.style.overflowY = 'auto';
  }, [height, quillRef]);

  const canUploadImage = Boolean(handleUploadImageDesc);
  const modules = useMemo(() => {
    const toolbar = [
      [{ font: Font.whitelist }, { size: Size.whitelist }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      canUploadImage ? ['link', 'image'] : ['link'],
      ['clean'],
    ];

    const handlers = canUploadImage
      ? {
          image: function imageHandler() {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/png,image/jpeg,image/webp');
            input.onchange = () => {
              const file = input.files?.[0];
              if (!file) return;
              imageUploadRef.current(
                { blob: () => file },
                (imageUrl) => {
                  if (!imageUrl || !/^https?:\/\//i.test(imageUrl)) {
                    setUploadErrorRef.current(field, {
                      type: 'upload',
                      message: 'Máy chủ không trả về đường dẫn hình ảnh hợp lệ.',
                    });
                    return;
                  }
                  const range = this.quill.getSelection(true);
                  this.quill.insertEmbed(range.index, 'image', imageUrl, 'user');
                  this.quill.setSelection(range.index + 1, 0, 'silent');
                },
                (error) => {
                  setUploadErrorRef.current(field, {
                    type: 'upload',
                    message: error?.message || 'Không thể tải hình ảnh lên.',
                  });
                },
              );
            };
            input.click();
          },
        }
      : undefined;

    return {
      toolbar: handlers ? { container: toolbar, handlers } : toolbar,
      clipboard: { matchVisual: false },
    };
  }, [canUploadImage, field]);

  const formats = [
    'font',
    'size',
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'color',
    'background',
    'script',
    'list',
    'bullet',
    'indent',
    'align',
    'link',
    'image',
    'clean',
  ];

  return (
    <div className={`quill-wrapper ${className}`} style={{ width }}>
      <ReactQuill
        ref={quillRef}
        theme='snow'
        modules={modules}
        formats={formats}
        disabled={disabled}
        readOnly={disabled}
        placeholder={placeholder}
        value={methods.watch(field) || ''}
        onChange={(value) => {
          methods.clearErrors(field);
          methods.setValue(field, value);
        }}
        style={{
          width: '100%',
          backgroundColor: 'white',
          color: '#000',
          minHeight: typeof height === 'number' ? `${height}px` : height,
        }}
      />
      {error && <ErrorMessage message={error?.message} />}
    </div>
  );
};

FormEditorQuill.propTypes = {
  field: PropTypes.string,
  validation: PropTypes.object,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.string,
  handleUploadImageDesc: PropTypes.func,
};

export default FormEditorQuill;
